import React, { useRef } from "react";
import {
  FilePlus2,
  Save,
  FolderOpen,
  Download,
  Printer,
  Layers,
  LogOut,
} from "lucide-react";
import { useProject } from "../state/projectStore";
import { useAuth } from "../auth/AuthContext";
import { downloadProject, deserializeProject } from "../lib/projectIO";
import { exportProjectToPDF, downloadPdfBytes, openPdfForPrint } from "../lib/pdfExport";
import { exportProjectToJPGZip } from "../lib/jpgExport";
import { toast } from "sonner";

const IconBtn = ({ onClick, icon: Icon, label, testid, primary }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={
      primary
        ? "h-8 px-3 text-xs font-medium rounded-sm bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 transition-colors flex items-center gap-2"
        : "h-8 px-3 text-xs font-medium rounded-sm bg-[#252A30] border border-[#31373E] text-[#F3F4F6] hover:bg-[#31373E] transition-colors flex items-center gap-2"
    }
    title={label}
  >
    <Icon size={14} strokeWidth={1.75} />
    <span className="hidden md:inline">{label}</span>
  </button>
);

export default function Toolbar() {
  const { state, newProject, loadProject } = useProject();
  const { username, logout } = useAuth();
  const fileInputRef = useRef(null);

  const handleNew = () => {
    if (window.confirm("¿Crear un proyecto nuevo? Se perderán los cambios no guardados.")) {
      newProject();
      toast.success("Proyecto nuevo creado");
    }
  };

  const handleSave = () => {
    downloadProject(state, "kapei-project.json");
    toast.success("Proyecto guardado");
  };

  const handleLoadClick = () => fileInputRef.current?.click();

  const handleLoadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const project = deserializeProject(text);
      loadProject(project);
      toast.success("Proyecto cargado");
    } catch (err) {
      toast.error("Error al cargar el proyecto: " + err.message);
    } finally {
      e.target.value = "";
    }
  };

  const handleExport = async () => {
    try {
      toast.loading("Generando PDF...", { id: "pdf-export" });
      const bytes = await exportProjectToPDF(state);
      const name = downloadPdfBytes(bytes);
      toast.success(`PDF descargado: ${name}`, { id: "pdf-export", duration: 5000 });
    } catch (err) {
      toast.error("Error al exportar: " + err.message, { id: "pdf-export" });
    }
  };
  const handleExportJPG = async () => {
    try {
      toast.loading("Generando JPG...", { id: "jpg-export" });
      await exportProjectToJPGZip(state);
      toast.success("JPG descargado en ZIP", { id: "jpg-export", duration: 5000 });
    } catch (err) {
      toast.error("Error al exportar JPG: " + err.message, { id: "jpg-export" });
    }
  };

  const handlePrint = async () => {
    try {
      toast.loading("Preparando impresión...", { id: "pdf-print" });
      const bytes = await exportProjectToPDF(state);
      const result = openPdfForPrint(bytes);
      if (result.popupBlocked) {
        toast.warning(
          `PDF descargado (${result.filename}). El navegador bloqueó la pestaña de impresión: ábrelo manualmente desde Descargas.`,
          { id: "pdf-print", duration: 7000 }
        );
      } else {
        toast.success(`PDF generado y abriendo diálogo de impresión (${result.filename})`, { id: "pdf-print", duration: 5000 });
      }
    } catch (err) {
      toast.error("Error al imprimir: " + err.message, { id: "pdf-print" });
    }
  };

  return (
    <div
      className="h-12 border-b border-[#31373E] bg-[#1C1F24] flex items-center px-3 justify-between shrink-0"
      data-testid="top-toolbar"
    >
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 mr-3">
          <Layers size={18} className="text-[#00E5FF]" strokeWidth={1.75} />
          <span className="text-sm font-semibold tracking-tight text-[#F3F4F6]">
            KAPEI Imposer <span className="text-[#00E5FF]">PRO</span>
          </span>
        </div>
        <div className="w-px h-6 bg-[#31373E]" />
        <IconBtn onClick={handleNew} icon={FilePlus2} label="Nuevo" testid="toolbar-new-btn" />
        <IconBtn onClick={handleSave} icon={Save} label="Guardar" testid="toolbar-save-btn" />
        <IconBtn onClick={handleLoadClick} icon={FolderOpen} label="Cargar" testid="toolbar-load-btn" />
        <input
          ref={fileInputRef}
          type="file"
          accept="application/json"
          className="hidden"
          onChange={handleLoadFile}
          data-testid="toolbar-load-input"
        />
      </div>
      <div className="flex items-center gap-2">
        <IconBtn onClick={handlePrint} icon={Printer} label="Imprimir" testid="toolbar-print-btn" />
        <IconBtn onClick={handleExportJPG} icon={Download} label="Exportar JPG" testid="toolbar-export-jpg-btn" />
        <IconBtn onClick={handleExport} icon={Download} label="Exportar PDF" testid="toolbar-export-btn" primary />
        <div className="w-px h-6 bg-[#31373E] ml-2 mr-1" />
        <span className="text-[10px] font-mono text-[#9CA3AF] hidden sm:inline" data-testid="toolbar-username">
          {username}
        </span>
        <button
          onClick={async () => {
            await logout();
            toast.success("Sesión cerrada");
          }}
          data-testid="toolbar-logout-btn"
          title="Cerrar sesión"
          className="h-8 px-2.5 text-xs font-medium rounded-sm bg-[#252A30] border border-[#31373E] text-[#9CA3AF] hover:text-[#FF3B30] hover:bg-[#2A1818] transition-colors flex items-center gap-1.5"
        >
          <LogOut size={14} strokeWidth={1.75} />
          <span className="hidden md:inline">Salir</span>
        </button>
      </div>
    </div>
  );
}
